import { Caption1, MessageBar, MessageBarBody, Spinner, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { formatRecordedCalendarDate, type InterviewRow } from '@vigeo/shared';
import { showingFirstCandidates } from '@/features/offers';
import { fetchCatalogOptions, fetchInterviews } from '@/lib/api';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  /** Page-name voice (24/700), as on Offers, People and Review. */
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  table: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalL, minWidth: 0,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  firstRow: { borderTop: 'none' },
  /** Offers' header row: the same flex bases as a row, hidden below tablet where rows stack and label themselves. */
  headerRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    paddingBlock: tokens.spacingVerticalS,
    borderTop: 'none',
    [shellMedia.belowTablet]: { display: 'none' },
  },
  headerCell: { fontWeight: tokens.fontWeightSemibold },
  candidate: {
    display: 'flex', flexDirection: 'column', minWidth: '13rem', flexBasis: '16rem', flexGrow: 1,
    // The name is a link; on a phone it gets the 44px target Offers gives its own (#331).
    [shellMedia.phone]: { '& a': { display: 'inline-flex', alignItems: 'center', minHeight: shellMetrics.touchTarget } },
  },
  when: { display: 'flex', flexDirection: 'column', minWidth: '9rem', flexBasis: '11rem' },
  who: { display: 'flex', flexDirection: 'column', minWidth: '11rem', flexBasis: '16rem', flexGrow: 1 },
});

/**
 * Interviews & orientation (#802, frame 06b's Hiring menu): every candidate's interview, from the
 * bounded cross-person read #303 added (`GET /api/interviews`).
 *
 * ONLY WHAT THE READ CARRIES. The row is a date and the interviewers, and nothing on this page derives
 * "overdue" or "this week" from them: the API leaves those calendar questions to a screen, and no
 * frame has decided them yet. Orientation is not on the read either, so the page says where it is
 * rather than drawing an empty column. The order is the API's: interviewed most recently first, then
 * the candidates with none recorded, then by name.
 *
 * Read-only. Recording an interview stays on the candidate's record, which every name links to.
 */
export function InterviewsPage() {
  const styles = useStyles();
  const interviews = useQuery({ queryKey: ['interviews'], queryFn: fetchInterviews });
  // Shared with Offers and the new-candidate form, so the role names cost no extra request.
  const catalog = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity });
  const roleLabels = new Map((catalog.data?.jobProfiles ?? []).map((profile) => [profile.key, profile.label]));

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Interviews & orientation</h1>
        <Caption1>Every candidate’s interview, the most recent first. Orientation is on each candidate’s record.</Caption1>
      </header>

      {interviews.isPending ? <Spinner size="small" label="Loading interviews" /> : null}

      {interviews.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>Interviews could not be loaded. Try again shortly.</MessageBarBody>
        </MessageBar>
      ) : null}

      {interviews.data === undefined ? null : (
        <>
          {/* The bound is the API's and arrives on the response (#300); the screen never assumes it. */}
          {interviews.data.truncated ? (
            <MessageBar intent="warning">
              <MessageBarBody>{showingFirstCandidates(interviews.data.counted)}</MessageBarBody>
            </MessageBar>
          ) : null}

          {interviews.data.rows.length === 0 ? (
            <MessageBar intent="info">
              <MessageBarBody>No candidates are in hiring right now.</MessageBarBody>
            </MessageBar>
          ) : (
            <div className={styles.table} role="list" aria-label="Interviews">
              <div className={mergeClasses(styles.row, styles.headerRow)} aria-hidden="true">
                <Text className={mergeClasses(styles.candidate, styles.headerCell)}>Candidate</Text>
                <Text className={mergeClasses(styles.when, styles.headerCell)}>Interviewed</Text>
                <Text className={mergeClasses(styles.who, styles.headerCell)}>Interviewers</Text>
              </div>
              {interviews.data.rows.map((row) => <InterviewListRow key={row.personId} row={row} roleLabels={roleLabels} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InterviewListRow({ row, roleLabels }: { readonly row: InterviewRow; readonly roleLabels: ReadonlyMap<string, string> }) {
  const styles = useStyles();
  // A key the catalog no longer names still shows, as itself, rather than vanishing from the row.
  const roles = row.jobProfileKeys.map((key) => roleLabels.get(key) ?? key).join(', ');
  return (
    <div className={styles.row} role="listitem">
      <div className={styles.candidate}>
        <Link to={`/app/people/${encodeURIComponent(row.personId)}`}>{row.candidateName}</Link>
        {roles === '' ? null : <Caption1>{roles}</Caption1>}
      </div>
      <div className={styles.when}>
        <Text>{row.interviewedOn === null ? 'No interview recorded' : formatRecordedCalendarDate(row.interviewedOn)}</Text>
      </div>
      <div className={styles.who}>
        <Text>{row.interviewers.length === 0 ? 'No interviewers recorded' : row.interviewers.map((interviewer) => interviewer.name).join(', ')}</Text>
      </div>
    </div>
  );
}
